import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

export async function fetchTerm(slug) {
  const { data } = await api.get(`/kb/term/${slug}`);
  return data;
}

export async function sendChat({ term, preload, message, history, sessionId }) {
  const { data } = await api.post(`/chat`, {
    term,
    preload,
    message,
    history: history || [],
    session_id: sessionId || null,
  });
  return data;
}

export function trackAction({ route, source, sourceCell, sourceState, audience }) {
  // Fire-and-forget analytics — persists to /api/analytics/action.
  // Failures never bubble up: this is decorative telemetry, not a user flow.
  const payload = {
    route,
    source: source || null,
    source_cell: sourceCell || null,
    source_state: sourceState || null,
    audience: audience || null,
  };
  // eslint-disable-next-line no-console
  console.log("[gg:action]", { ...payload, ts: new Date().toISOString() });
  api.post("/analytics/action", payload).catch(() => {
    /* swallow — telemetry only */
  });
}

/**
 * Submit an RFP from the /audit page. Uses multipart/form-data because we
 * may be sending attachments (bill PDFs, load CSVs, site photos).
 * On success the backend returns { ref_id, status, message, received_at }.
 */
export async function submitRfp({ contact, variant, inputs, files }) {
  const fd = new FormData();
  fd.append("name", contact.name);
  fd.append("org", contact.org);
  fd.append("role", contact.role || "");
  fd.append("email", contact.email);
  fd.append("phone", contact.phone);
  fd.append("variant", variant || "sb");
  fd.append("inputs_json", JSON.stringify(inputs || {}));
  (files || []).forEach((f) => {
    fd.append("files", f, f.name);
  });
  const { data } = await api.post("/rfp", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

// ---------- Data Room (Phase 4) ----------
export async function submitAccessRequest({ fields, files }) {
  const fd = new FormData();
  Object.entries(fields).forEach(([k, v]) => fd.append(k, v ?? ""));
  (files || []).forEach((f) => fd.append("files", f, f.name));
  const { data } = await api.post("/data-room/access-request", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

export async function redeemDataRoomToken(token) {
  const { data } = await api.post("/data-room/redeem", { token });
  return data;
}

export async function getDataRoomSession() {
  try {
    const { data } = await api.get("/data-room/session");
    return data;
  } catch (err) {
    if (err?.response?.status === 401) return null;
    throw err;
  }
}

export async function logoutDataRoom() {
  await api.post("/data-room/logout");
}

export async function submitPartnershipRfp({ fields, termSheet }) {
  const fd = new FormData();
  Object.entries(fields).forEach(([k, v]) => fd.append(k, v ?? ""));
  if (termSheet) fd.append("term_sheet", termSheet, termSheet.name);
  const { data } = await api.post("/data-room/partnership-rfp", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

// ---------- Phase 5: partners / reserve / visit ----------
export async function registerDeal(payload) {
  const { data } = await api.post("/deals/register", payload);
  return data;
}
export async function listCommunityProjects() {
  const { data } = await api.get("/community-projects");
  return data.projects || [];
}
export async function submitReservation(payload) {
  const { data } = await api.post("/reserve/block", payload);
  return data;
}
export async function bookVisit(payload) {
  const { data } = await api.post("/visits/book", payload);
  return data;
}

// ---------- Phase 7: admin cockpit + public momentum ----------

export async function fetchPartnerMomentum() {
  const { data } = await api.get("/partner/momentum");
  return data;
}

function adminHeaders(token) {
  return { headers: { Authorization: `Bearer ${token}` } };
}

export async function adminHealth(token) {
  const { data } = await api.get("/admin/health", adminHeaders(token));
  return data;
}

export async function adminList(token, kind, { limit = 50, offset = 0 } = {}) {
  // kind ∈ "rfp" | "data-room/requests" | "data-room/partnerships" | "deals" | "reservations" | "visits"
  const { data } = await api.get(`/admin/${kind}?limit=${limit}&offset=${offset}`, adminHeaders(token));
  return data;
}

export async function adminApproveDataRoom(token, refId) {
  const { data } = await api.post("/admin/data-room/approve", { ref_id: refId }, adminHeaders(token));
  return data;
}

export async function adminRejectDataRoom(token, refId, reason) {
  const { data } = await api.post(
    "/admin/data-room/reject",
    { ref_id: refId, reason: reason || null },
    adminHeaders(token),
  );
  return data;
}

export async function adminLogs(token, name) {
  const { data } = await api.get(`/admin/logs/${name}`, adminHeaders(token));
  return data;
}
